import { useState } from 'react';
import { Mail, Lock, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { User } from '../App';

interface LoginScreenProps {
  onLogin: (user: User) => void;
}

// Email to display name mapping (used for Google OAuth)
export const EMAIL_NAME_MAPPING: Record<string, string> = {
  'admin@immerseindiatours.com': 'Admin User',
  'info@immerseindiatours.com': 'Info Admin',
  'madhu.b@immerseindiatours.com': 'Madhu Chaudhary',
  'ujjwal@immerseindiatours.com': 'Ujjwal',
  'ketan.gupta@immerseindiatours.com': 'Ketan Gupta',
  'operations@immerseindiatours.com': 'Jitendra Yadav',
  'sunil.verma@immerseindiatours.com': 'Sunil Verma',
  'nidhi@immerseindiatours.com': 'Nidhi Gopal',
  'anima.biswal@immerseindiatours.com': 'Anima Biswal',
  'pamlesh.rana@immerseindiatours.com': 'Pamlesh Rana',
  'sumesh@immerseindiatours.com': 'Sumesh Sudharasan',
  'shambu@immerseindiatours.com': 'Shambu Rawat',
  'tarun.chauhan@immerseindiatours.com': 'Tarun Chauhan',
  'arjun.sahni@immerseindiatours.com': 'Arjun Sahni'
};

// IMPORTANT: Only admin has dummy password. Managers must use real passwords or Google OAuth.
// This is for reference only - actual auth happens on backend
const USERS = [
  { id: 'admin-001', email: 'admin@immerseindiatours.com', password: 'admin123', name: 'Admin User', role: 'admin' as const, isAdmin: true },
  { id: 'admin-002', email: 'info@immerseindiatours.com', password: 'admin123', name: 'Info Admin', role: 'admin' as const, isAdmin: true },
];

export function LoginScreen({ onLogin }: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();
      
      if (data.success && data.data?.token) {
        // Store JWT token
        localStorage.setItem('authToken', data.data.token);
        localStorage.setItem('token', data.data.token); // For backward compatibility
        
        // Call onLogin with user data
        // Parse assignedOperators from user data
        let assignedOps: string[] = [];
        try {
          if (data.data.user.assignedOperators) {
            assignedOps = typeof data.data.user.assignedOperators === 'string'
              ? JSON.parse(data.data.user.assignedOperators)
              : data.data.user.assignedOperators;
          }
        } catch (e) { /* ignore */ }
        onLogin({
          id: data.data.user.id,
          name: data.data.user.name,
          role: data.data.user.role === 'ADMIN' ? 'admin' : 'manager',
          isAdmin: data.data.user.role === 'ADMIN',
          email: data.data.user.email,
          assignedOperators: assignedOps,
          managerId: data.data.user.managerId || null
        });
      } else {
        setError(data.message || 'Invalid email or password');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // Redirect to backend Google OAuth
      window.location.href = `${API_URL}/auth/google/login`;
    } catch (error) {
      console.error('Google login error:', error);
      setError('Failed to connect to server');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-purple-800 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo and Branding */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center">
            <img 
              src="/logo1.png" 
              alt="Company Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-semibold text-white mb-2">CRM</h1>
          <p className="text-purple-200">Sign in to your account</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="w-full mt-4 border-gray-300 hover:bg-gray-50"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </Button>
          </div>
        </div>

        <p className="text-center text-purple-200 text-sm mt-6">
          © 2025 Tour Operations CRM. All rights reserved.
        </p>
      </motion.div>
    </div>
  );
}
